import { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Layers, User, Loader2, Trophy } from 'lucide-react';
import { Tutee } from '../../types/tuition';
import { fetchStudentsForTutee } from '../../services/tuteeService';
import { fetchTuteeComponents } from '../../services/componentService';
import {
  fetchFlashcardStats,
  fetchFlashcardDecks,
  fetchFlashcards,
  parseFlashcardConfig,
  resolveStudentSlug,
  type FlashcardDeck,
  type FlashcardModuleConfig,
  type FlashcardStats,
  type FlashcardStudyScope,
} from '../../services/flashcardService';
import FlashcardReview from './FlashcardReview';
import FlashcardsProgress from './FlashcardsProgress';
import FlashcardDeckPicker, { studyScopeLabel } from './FlashcardDeckPicker';
import FlashcardsNavTabs, { type FlashcardsTab } from './FlashcardsNavTabs';
import FlashcardsMyCards from './FlashcardsMyCards';

type View = FlashcardsTab | 'study' | 'summary';

interface FlashcardsAppProps {
  tutee: Tutee;
  onBack: () => void;
  config?: Record<string, unknown>;
}

const FlashcardsApp = ({ tutee, onBack, config: configProp }: FlashcardsAppProps) => {
  const [view, setView] = useState<View>('home');
  const [students, setStudents] = useState<{ value: string; label: string }[]>([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [hasChosenStudent, setHasChosenStudent] = useState(false);
  const [moduleConfig, setModuleConfig] = useState<FlashcardModuleConfig>(() =>
    parseFlashcardConfig(configProp)
  );
  const [stats, setStats] = useState<FlashcardStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [sessionReviewed, setSessionReviewed] = useState(0);
  const [decks, setDecks] = useState<FlashcardDeck[]>([]);
  const [studyScope, setStudyScope] = useState<FlashcardStudyScope>('all');
  const [hasUnassignedCards, setHasUnassignedCards] = useState(false);

  const activeTab: FlashcardsTab | null =
    view === 'home' || view === 'progress' || view === 'mycards' ? view : null;

  useEffect(() => {
    const load = async () => {
      const list = await fetchStudentsForTutee(tutee.id);
      const opts = list.map((s) => ({
        value: s.studentName.toLowerCase(),
        label: s.studentName.charAt(0).toUpperCase() + s.studentName.slice(1),
      }));
      setStudents(opts);
      if (opts.length === 1) {
        setSelectedStudent(opts[0].value);
        setHasChosenStudent(true);
      } else if (opts.length === 0) {
        setSelectedStudent(tutee.id);
        setHasChosenStudent(true);
      }
    };
    load();
  }, [tutee.id]);

  useEffect(() => {
    if (!configProp) {
      fetchTuteeComponents(tutee.id).then((comps) => {
        const fc = comps.find((c) => c.component?.componentType === 'flashcards');
        if (fc?.config) setModuleConfig(parseFlashcardConfig(fc.config));
      });
    }
  }, [tutee.id, configProp]);

  const studentSlug = useMemo(
    () =>
      resolveStudentSlug(
        tutee.id,
        students.map((s) => ({ studentName: s.value })),
        selectedStudent
      ),
    [tutee.id, students, selectedStudent]
  );

  const studyScopeStorageKey =
    studentSlug ? `flashcard_study_scope_${tutee.id}_${studentSlug}` : null;

  useEffect(() => {
    if (!hasChosenStudent || !studentSlug) return;
    let cancelled = false;
    (async () => {
      try {
        const [deckList, cards] = await Promise.all([
          fetchFlashcardDecks(tutee.id),
          fetchFlashcards(tutee.id, studentSlug),
        ]);
        if (cancelled) return;
        setDecks(deckList.filter((d) => d.status === 'active'));
        setHasUnassignedCards(cards.some((c) => !c.deckId));

        const saved = studyScopeStorageKey ? localStorage.getItem(studyScopeStorageKey) : null;
        if (saved === 'all' || saved === 'unassigned') {
          setStudyScope(saved);
        } else if (saved && deckList.some((d) => d.id === saved)) {
          setStudyScope(saved);
        } else {
          setStudyScope('all');
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tutee.id, hasChosenStudent, studentSlug, studyScopeStorageKey]);

  useEffect(() => {
    if (!studyScopeStorageKey) return;
    localStorage.setItem(studyScopeStorageKey, studyScope);
  }, [studyScope, studyScopeStorageKey]);

  const scopeLabel = studyScopeLabel(studyScope, decks);

  const refreshStats = async () => {
    if (!studentSlug) return;
    setLoadingStats(true);
    try {
      const s = await fetchFlashcardStats(tutee.id, studentSlug, studyScope);
      setStats(s);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    if (hasChosenStudent && studentSlug && activeTab) refreshStats();
  }, [hasChosenStudent, studentSlug, studyScope, activeTab]);

  const goToTab = (tab: FlashcardsTab) => setView(tab);

  if (!hasChosenStudent && students.length > 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 p-6">
        <button onClick={onBack} className="flex items-center gap-2 text-indigo-600 font-bold mb-8">
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>
        <h2 className="text-2xl font-black text-gray-900 mb-6">Who is studying?</h2>
        <div className="grid gap-3 max-w-md">
          {students.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => {
                setSelectedStudent(s.value);
                setHasChosenStudent(true);
              }}
              className="flex items-center gap-3 p-4 bg-white rounded-2xl shadow-lg font-bold text-gray-800 hover:shadow-xl active:scale-[0.98]"
            >
              <User className="w-6 h-6 text-indigo-500" />
              {s.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 pb-20">
      <div className="max-w-3xl mx-auto px-4 pt-4 sm:pt-6">
        <button
          onClick={view === 'home' ? onBack : () => setView('home')}
          className="flex items-center gap-2 text-indigo-600 font-bold mb-4 active:scale-95"
        >
          <ArrowLeft className="w-5 h-5" />
          {view === 'home' ? 'Back to dashboard' : 'Flashcards home'}
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className={`p-3 rounded-xl bg-gradient-to-br ${tutee.colorScheme.gradient} shadow-lg`}>
            <Layers className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-gray-900">Flashcards</h1>
            <p className="text-sm text-gray-600">Spaced repetition · SM-2</p>
          </div>
        </div>

        {activeTab && (
          <>
            {students.length > 1 && (
              <div className="mb-4 flex flex-wrap gap-2">
                {students.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setSelectedStudent(s.value)}
                    className={`px-3 py-1.5 rounded-full text-sm font-bold ${
                      selectedStudent === s.value
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white/70 text-gray-600'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            <FlashcardsNavTabs tutee={tutee} active={activeTab} onChange={goToTab} />
          </>
        )}

        {(view === 'home' || view === 'progress') && (
          <FlashcardDeckPicker
            tutee={tutee}
            decks={decks}
            studyScope={studyScope}
            onStudyScopeChange={setStudyScope}
            showUnassigned={hasUnassignedCards}
          />
        )}

        {view === 'home' && (
          <>
            {loadingStats ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
              </div>
            ) : stats ? (
              <FlashcardsProgress
                tutee={tutee}
                stats={stats}
                mode="home"
                scopeLabel={studyScope !== 'all' ? scopeLabel : undefined}
                onStartStudy={() => setView('study')}
              />
            ) : null}
          </>
        )}

        {view === 'study' && (
          <>
            {studyScope !== 'all' && (
              <p className="mb-4 text-sm font-semibold text-gray-600 bg-white/70 rounded-xl px-4 py-2 border border-white/60">
                Studying: <span className="text-gray-900">{scopeLabel}</span>
              </p>
            )}
            <FlashcardReview
              tutee={tutee}
              studentSlug={studentSlug}
              moduleConfig={moduleConfig}
              studyScope={studyScope}
              onSessionEnd={(n) => {
                setSessionReviewed(n);
                setView('summary');
                refreshStats();
              }}
              onEmpty={() => setView('home')}
            />
          </>
        )}

        {view === 'progress' && (
          <>
            {loadingStats ? (
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-500" />
            ) : stats ? (
              <FlashcardsProgress
                tutee={tutee}
                stats={stats}
                mode="analytics"
                scopeLabel={studyScope !== 'all' ? scopeLabel : undefined}
                onStartStudy={() => setView('study')}
              />
            ) : null}
          </>
        )}

        {view === 'mycards' && (
          <FlashcardsMyCards
            tutee={tutee}
            studentSlug={studentSlug}
            allowManage={moduleConfig.allowStudentCreatedCards}
            onCardsChanged={refreshStats}
          />
        )}

        {view === 'summary' && (
          <div className="text-center py-12 animate-fade-in-up">
            <Trophy className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-black text-gray-900 mb-2">Session complete!</h2>
            <p className="text-gray-600 mb-8">You reviewed {sessionReviewed} cards. Great work.</p>
            <button
              type="button"
              onClick={() => setView('home')}
              className={`px-8 py-3 rounded-xl font-bold text-white bg-gradient-to-r ${tutee.colorScheme.gradient}`}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlashcardsApp;
